import {isFeatureEnabled} from 'feature-flags';
import {Menu} from 'platform/components';

import {useLocation} from 'react-router-dom';

import {isNotNil} from 'ramda';

import {
  useGetMultiFactorAuthenticationSettingsQuery,
  useGetMyMultiFactorAuthenticationSettingsQuery,
} from '@dms/api/base';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {myProfileRoutes} from '@dms/routes';
import {NavigationItem, usePermissions, useUserExternalSystems} from '@dms/shared';

import {buildArray, useNavigate} from 'shared/web';

export function MyProfileNavigation() {
  const navigate = useNavigate();
  const {pathname} = useLocation();

  const {data: mfaSettings} = useGetMultiFactorAuthenticationSettingsQuery();
  const {data: userMfaSettings} = useGetMyMultiFactorAuthenticationSettingsQuery();
  const hasCurrentUserActiveMfa = isNotNil(userMfaSettings?.activatedAt);
  const isCurrentUserMfaDeactivated = isNotNil(userMfaSettings?.deactivatedAt);

  const [canReadMfa] = usePermissions({
    permissionKeys: ['canReadUserMultiFactorAuthenticationSettings'],
  });

  const {data: currentUser} = useGetCurrentUserInfoQuery();
  const {hasExternalSystems} = useUserExternalSystems(currentUser?.id);

  const menuItems = buildArray<NavigationItem>([
    {
      id: 'my-profile-personal-information',
      label: i18n.t('entity.user.labels.personalInformation'),
      href: myProfileRoutes.personalInformation,
      leftIcon: 'social/person',
    },
    {
      id: 'my-profile-security',
      label: i18n.t('entity.user.labels.security'),
      href: myProfileRoutes.security,
      leftIcon: 'hardware/security',
    },
  ])
    .whenFeatureEnabled(featureFlags.USER_LOGIN_HISTORY, {
      id: 'my-profile-login-history',
      label: i18n.t('entity.user.labels.loginHistory'),
      href: myProfileRoutes.loginHistory,
      leftIcon: 'action/history',
    })
    // Dashboard customisation moves widget visibility onto the dashboard itself.
    .whenNot(isFeatureEnabled(featureFlags.DASHBOARD_CUSTOMISATION), {
      id: 'my-profile-dashboard',
      label: i18n.t('page.myProfile.dashboard.sidebarTitle'),
      href: myProfileRoutes.dashboard,
      leftIcon: 'action/dashboard',
    })
    .add({
      id: 'my-profile-language',
      label: i18n.t('entity.user.labels.langAndTime'),
      href: myProfileRoutes.language,
      leftIcon: 'action/language',
    })
    .add({
      id: 'my-profile-notifications',
      label: i18n.t('page.settings.notifications.title'),
      href: myProfileRoutes.notifications,
      leftIcon: 'alert/notification_important',
    })
    .add({
      id: 'my-profile-email-signature',
      label: i18n.t('page.myProfile.labels.emailSignature'),
      href: myProfileRoutes.emailSignature,
      leftIcon: 'content/mail',
    })
    .add({
      id: 'my-profile-email-emailServices',
      label: i18n.t('page.myProfile.labels.emailServices'),
      href: myProfileRoutes.emailServices,
      leftIcon: 'content/mail',
    })
    .when(hasExternalSystems, {
      id: 'my-profile-external-systems',
      label: i18n.t('page.employeeDetail.labels.externalAuthorizationSystem'),
      href: myProfileRoutes.externalSystems,
      leftIcon: 'content/next_week',
    })
    .add({
      id: 'my-profile-table-exports',
      label: i18n.t('page.myProfile.labels.tableExports'),
      href: myProfileRoutes.tableExports,
      leftIcon: 'file/download',
    })
    .whenFeatureEnabled(featureFlags.USER_ABSENCE, {
      id: 'my-profile-user-absence',
      label: i18n.t('user.awayStatus.awayBadge'),
      href: myProfileRoutes.userAbsence,
      leftIcon: 'action/calendar_today',
    })
    .when(isFeatureEnabled(featureFlags.OUTLOOK_INTEGRATION), {
      id: 'my-profile-calendar-synchronization',
      label: i18n.t('page.myProfile.labels.calendarSynchronization'),
      href: myProfileRoutes.calendarSynchronization,
      leftIcon: 'action/calendar_today',
    })
    .when(
      !isCurrentUserMfaDeactivated &&
        canReadMfa &&
        mfaSettings?.active &&
        isFeatureEnabled(featureFlags.SETTINGS_TWO_FACTOR_AUTH),
      {
        id: 'my-profile-multi-factor-authentication',
        label: i18n.t('page.settings.labels.multiFactorAuth'),
        href: myProfileRoutes.multiFactorAuthentication,
        leftIcon: 'action/lock',
        badge: hasCurrentUserActiveMfa
          ? {colorScheme: 'green', icon: 'navigation/check'}
          : {colorScheme: 'red', icon: 'navigation/close'},
      }
    );

  const allItems = menuItems;
  const activeItem = allItems.find((item) => item.href && pathname.includes(item.href));

  const handleItemChange = (id: string) => {
    const item = allItems.find((item) => item.id === id);
    if (item?.href) {
      navigate(item.href);
    }
  };

  return (
    <Menu
      items={menuItems}
      activeItemId={activeItem?.id}
      onItemChange={handleItemChange}
      isSearchable
      data-testid="menu"
    />
  );
}
